import { Axis, AxisLeft, Orientation } from '@visx/axis'
import { curveLinear } from '@visx/curve'
import { LinearGradient } from '@visx/gradient'
import { Grid } from '@visx/grid'
import { Group } from '@visx/group'
import { ParentSize } from '@visx/responsive'
import { scaleLinear, scaleUtc } from '@visx/scale'
import { LinePath } from '@visx/shape'
import { useTooltipInPortal } from '@visx/tooltip'
import { NumberValue } from '@visx/vendor/d3-scale'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { ComparisonLineChartDataType } from 'src/components/analytics/types/comparison-line-chart'
import { SSOAnalyticsTimeScale } from 'src/components/sso-analytics/charts/common/types'
import { formatINRShort } from 'src/lib/utils/analytics'
import { BellRinging } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'

export enum ChartVariant {
  DEFAULT = 'default',
  SHOPASS = 'shopass'
}

type ChartColors = {
  primary: string
  pointFillClass: string
  strokeColor: string
}

const CHART_COLORS: Record<ChartVariant, ChartColors> = {
  [ChartVariant.DEFAULT]: {
    primary: '#01CCA7',
    pointFillClass: 'bg-primary-dark',
    strokeColor: 'stroke-primary-dark'
  },
  [ChartVariant.SHOPASS]: {
    primary: '#6535FF',
    pointFillClass: 'bg-violet-dark',
    strokeColor: 'stroke-violet-dark'
  }
}

const margin = {
  top: 10,
  bottom: 40,
  left: 60,
  right: 20
}

function LineChart({
  width,
  height,
  data,
  isNoComparison,
  selectedTimeScale,
  tooltipTitle,
  yScaleType,
  isCurrency,
  variant = ChartVariant.DEFAULT,
  thresholdAlertConfig
}: {
  width: number
  height: number
  data: ComparisonLineChartDataType
  isNoComparison: boolean
  selectedTimeScale: SSOAnalyticsTimeScale
  tooltipTitle: string
  yScaleType: 'percentage' | 'linear'
  isCurrency?: boolean
  variant?: ChartVariant
  thresholdAlertConfig?: {
    isAlertsSupported: boolean
    onClickAlert: (rate: number) => void
  }
}) {
  // Chart dimensions
  const xMax = width - (margin.left + margin.right)
  const yMax = height - (margin.top + margin.bottom)

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true
  })

  const [hoverInfo, setHoverInfo] = useState<{
    xPos: number
    yPos: number
    date: Date
    currentPoint?: { rate: number; y: number; actualDate: Date }
    previousPoint?: { rate: number; y: number; actualDate: Date }
  } | null>(null)

  const { primary, pointFillClass, strokeColor } = CHART_COLORS[variant]

  // Normalize data - convert rates to numbers, dates to Date objects, and filter out null/undefined values
  const normalizedData = useMemo(() => {
    const normalizeDataset = (dataset: typeof data.currentWeekData) =>
      dataset
        .filter(d => d.rate != null)
        .map(d => ({
          ...d,
          rate: Number(d.rate) || 0,
          displayDate: new Date(d.displayDate),
          actualDate: new Date(d.actualDate)
        }))

    return {
      currentWeekData: normalizeDataset(data.currentWeekData),
      previousWeekData: normalizeDataset(data.previousWeekData),
      primaryDataset: data.primaryDataset
    }
  }, [data])

  const xScale = useMemo(
    () =>
      scaleUtc({
        domain: [
          new Date(data.primaryDataset[0]?.date ?? new Date()),
          new Date(data.primaryDataset[data.primaryDataset.length - 1]?.date ?? new Date())
        ],
        range: [10, xMax]
      }),
    [xMax, data.primaryDataset]
  )

  const yScale = useMemo(() => {
    // Use normalized data which already has valid number rates
    const maxCurrentRate =
      normalizedData.currentWeekData.length > 0 ? Math.max(...normalizedData.currentWeekData.map(d => d.rate)) : 0
    const maxPreviousRate =
      normalizedData.previousWeekData.length > 0 ? Math.max(...normalizedData.previousWeekData.map(d => d.rate)) : 0
    const maxRate = Math.max(maxCurrentRate, maxPreviousRate)

    // Round up to the nearest multiple of 10 that's greater than maxRate
    const yMax = Math.ceil(maxRate / 10) * 10

    return scaleLinear<number>({
      domain: [0, Math.max(yMax, 20)], // Use at least 20 as the maximum
      range: [height - margin.top - margin.bottom, 0],
      nice: true
    })
  }, [normalizedData, height])

  const handleMouseMove = (event: React.MouseEvent) => {
    const { left } = event.currentTarget.getBoundingClientRect()
    const mouseX = event.clientX - left - margin.left
    const mouseY = event.clientY - margin.top

    if (mouseX < 0 || mouseX > xMax) {
      setHoverInfo(null)
      return
    }

    const hoverDate = xScale.invert(mouseX)

    // Find the nearest point with snap zones - dataset already has valid rates from normalizedData
    const findNearestPoint = (dataset: typeof normalizedData.currentWeekData) => {
      if (dataset.length === 0) return null

      if (dataset.length === 1) {
        const point = dataset[0]
        const pointX = xScale(point.displayDate)
        const snapThreshold = pointX / 2
        const distance = Math.abs(mouseX - pointX)
        if (distance <= snapThreshold) {
          return {
            rate: point.rate,
            x: pointX,
            y: yScale(point.rate),
            displayDate: point.displayDate,
            actualDate: point.actualDate
          }
        }
        return null
      }

      // Find the closest point
      let nearestPoint = dataset[0]
      let minDistance = Infinity

      for (let i = 0; i < dataset.length; i++) {
        const point = dataset[i]
        const pointX = xScale(point.displayDate)
        const distance = Math.abs(mouseX - pointX)

        if (distance < minDistance) {
          minDistance = distance
          nearestPoint = point
        }
      }

      // Only return the point if we're within a reasonable snap distance
      // (half the distance to the next/previous point)
      let snapThreshold: number
      const pointIndex = dataset.indexOf(nearestPoint)

      if (pointIndex === 0) {
        // First point - use distance to next point
        const nextPointX = xScale(dataset[1].displayDate)
        const currentPointX = xScale(dataset[0].displayDate)
        snapThreshold = (nextPointX - currentPointX) / 2
      } else if (pointIndex === dataset.length - 1) {
        // Last point - use distance to previous point
        const prevPointX = xScale(dataset[dataset.length - 2].displayDate)
        const currentPointX = xScale(dataset[dataset.length - 1].displayDate)
        snapThreshold = (currentPointX - prevPointX) / 2
      } else {
        // Middle points - use average of distances to adjacent points
        const prevPointX = xScale(dataset[pointIndex - 1].displayDate)
        const currentPointX = xScale(nearestPoint.displayDate)
        const nextPointX = xScale(dataset[pointIndex + 1].displayDate)
        const leftSpacing = currentPointX - prevPointX
        const rightSpacing = nextPointX - currentPointX
        snapThreshold = Math.min(leftSpacing, rightSpacing) / 2
      }

      if (minDistance <= snapThreshold) {
        return {
          rate: nearestPoint.rate,
          x: xScale(nearestPoint.displayDate),
          y: yScale(nearestPoint.rate),
          displayDate: nearestPoint.displayDate,
          actualDate: nearestPoint.actualDate
        }
      }

      return null
    }

    const currentPoint = findNearestPoint(normalizedData.currentWeekData)
    const previousPoint = findNearestPoint(normalizedData.previousWeekData)

    if (currentPoint || previousPoint) {
      setHoverInfo({
        xPos: currentPoint?.x || previousPoint?.x || 0,
        yPos: currentPoint?.y || previousPoint?.y || yScale(0),
        date: (currentPoint || previousPoint)?.actualDate || hoverDate,
        currentPoint: currentPoint
          ? {
              rate: currentPoint.rate,
              y: currentPoint.y,
              actualDate: currentPoint.actualDate
            }
          : undefined,
        previousPoint: previousPoint
          ? {
              rate: previousPoint.rate,
              y: previousPoint.y,
              actualDate: previousPoint.actualDate
            }
          : undefined
      })
    } else {
      setHoverInfo(null)
    }
  }

  const handleMouseLeave = () => {
    setHoverInfo(null)
  }

  const handleClick = () => {
    if (thresholdAlertConfig?.isAlertsSupported && hoverInfo?.currentPoint?.rate) {
      thresholdAlertConfig.onClickAlert(hoverInfo?.currentPoint?.rate)
    }

    if (thresholdAlertConfig?.isAlertsSupported && hoverInfo?.previousPoint?.rate) {
      thresholdAlertConfig.onClickAlert(hoverInfo?.previousPoint?.rate)
    }
  }

  const tickFormatX = (value: Date | NumberValue) => {
    if (selectedTimeScale === 'hourly') {
      return format(value as Date, 'HH:mm')
    }
    return format(value as Date, 'MMM dd')
  }

  const formatTooltipDate = (date: Date) => {
    if (selectedTimeScale === 'hourly') {
      return format(date, 'EEE, MMM dd yyyy HH:mm')
    }
    return format(date, 'EEE, MMM dd yyyy')
  }

  // Modify the X-axis configuration
  const getOptimalTickCount = (width: number) => {
    if (selectedTimeScale === 'hourly') {
      return Math.min(8, Math.floor(width / 80)) // Show fewer ticks for hourly view
    }
    return Math.min(12, Math.floor(width / 100))
  }

  const tickFormat = (v: NumberValue) => {
    if (isCurrency) {
      return formatINRShort(v as number)
    }
    return yScaleType === 'percentage' ? `${v}%` : v.toString()
  }

  const tooltipFormatter = (v: NumberValue) => {
    if (isCurrency) {
      return v.toLocaleString('en-IN', { maximumFractionDigits: 2, style: 'currency', currency: 'INR' })
    }
    return yScaleType === 'percentage' ? `${v}%` : v.toLocaleString('en-IN', { maximumFractionDigits: 2 })
  }

  return (
    <div className='h-full'>
      <svg
        ref={containerRef}
        width={width}
        height={height}
        style={{ overflow: 'visible' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className={thresholdAlertConfig?.isAlertsSupported && hoverInfo ? 'cursor-pointer' : ''}
      >
        <Group left={margin.left} top={margin.top}>
          {/* Y-axis */}
          <Grid xScale={xScale} yScale={yScale} width={xMax} height={yMax} numTicksRows={4} numTicksColumns={0} />
          <LinearGradient id='current-line-gradient' from={primary} fromOpacity={0.2} to='#fff' toOpacity={0.04} />
          <AxisLeft
            scale={yScale}
            tickFormat={tickFormat}
            numTicks={4}
            stroke='transparent'
            tickStroke='transparent'
            tickLabelProps={() => ({
              fill: '#949494',
              fontSize: 12,
              textAnchor: 'end',
              dx: -8
            })}
          />
          <Axis
            orientation={Orientation.bottom}
            top={yMax}
            scale={xScale}
            stroke='transparent'
            tickFormat={tickFormatX}
            tickStroke='transparent'
            tickLabelProps={() => ({
              fill: '#949494',
              fontSize: 12,
              textAnchor: 'end',
              dx: 0
            })}
            numTicks={getOptimalTickCount(xMax)}
          />

          {/* Previous week line */}
          {!isNoComparison && normalizedData.previousWeekData.length > 0 && (
            <LinePath
              data={normalizedData.previousWeekData}
              x={d => xScale(d.displayDate)}
              y={d => yScale(d.rate)}
              stroke='#ADAFAF'
              strokeWidth={1}
              strokeDasharray='4,4'
              curve={curveLinear}
            />
          )}
          {/* Add area fill under current week line */}
          {normalizedData.currentWeekData.length > 0 && (
            <LinePath
              data={[
                ...normalizedData.currentWeekData,
                { ...normalizedData.currentWeekData[normalizedData.currentWeekData.length - 1], rate: 0 },
                { ...normalizedData.currentWeekData[0], rate: 0 }
              ]}
              x={d => xScale(d.displayDate)}
              y={d => yScale(d.rate)}
              fill='url(#current-line-gradient)'
              curve={curveLinear}
            />
          )}

          {/* Current week line */}
          {normalizedData.currentWeekData.length > 0 && (
            <LinePath
              data={normalizedData.currentWeekData}
              x={d => xScale(d.displayDate)}
              y={d => yScale(d.rate)}
              stroke={primary}
              className={strokeColor}
              strokeWidth={1}
              curve={curveLinear}
            />
          )}
          {/* Always show single points - only if rate is valid */}
          {normalizedData.currentWeekData.length === 1 && (
            <circle
              cx={xScale(normalizedData.currentWeekData[0].displayDate)}
              cy={yScale(normalizedData.currentWeekData[0].rate)}
              r={4}
              fill={primary}
              stroke='white'
              strokeWidth={2}
            />
          )}
          {normalizedData.previousWeekData.length === 1 && (
            <circle
              cx={xScale(normalizedData.previousWeekData[0].displayDate)}
              cy={yScale(normalizedData.previousWeekData[0].rate)}
              r={4}
              fill='#666666'
              stroke='white'
              strokeWidth={2}
            />
          )}
          {hoverInfo && (
            <>
              {/* Vertical line */}
              <line x1={hoverInfo.xPos} x2={hoverInfo.xPos} y1={0} y2={yMax} stroke='#ADAFAF' pointerEvents='none' />

              {/* Current week point */}
              {hoverInfo.currentPoint && (
                <circle
                  cx={hoverInfo.xPos}
                  cy={hoverInfo.currentPoint.y}
                  r={4}
                  fill={primary}
                  stroke='white'
                  strokeWidth={2}
                  pointerEvents='none'
                />
              )}

              {/* Previous week point */}
              {hoverInfo.previousPoint && (
                <circle
                  cx={hoverInfo.xPos}
                  cy={hoverInfo.previousPoint.y}
                  r={4}
                  fill='#666666'
                  stroke='white'
                  strokeWidth={2}
                  pointerEvents='none'
                />
              )}
            </>
          )}
        </Group>
        {/* Tooltip */}
      </svg>

      {hoverInfo && (
        <TooltipInPortal
          key={Math.random()}
          top={hoverInfo.yPos - 50}
          left={hoverInfo.xPos + margin.left + 10}
          style={{
            boxShadow: '0px 0px 12px 0px #0000001A',
            pointerEvents: 'none'
          }}
          className='bg-white absolute z-1000 rounded-lg shadow-md w-fit flex flex-col overflow-hidden'
        >
          <div className='p-4'>
            <div className='mb-2'>
              <div className='text-carbon text-sm font-medium'>{tooltipTitle}</div>
            </div>
            <div className='flex flex-col gap-4'>
              {hoverInfo.currentPoint && (
                <div className='flex items-center justify-between min-w-[200px]'>
                  <div className='mb-2'>
                    <div className='text-coal-light text-xs font-medium'>
                      {formatTooltipDate(hoverInfo.currentPoint.actualDate)}
                    </div>
                    <div className='text-carbon text-sm font-medium'>
                      {tooltipFormatter(hoverInfo.currentPoint.rate)}
                    </div>
                  </div>

                  <div className={`w-8 h-1 rounded-full ${pointFillClass}`}></div>
                </div>
              )}
              {hoverInfo.previousPoint && !isNoComparison && (
                <div className='flex items-center justify-between min-w-[200px]'>
                  <div>
                    <div className='text-coal-light text-xs font-medium'>
                      {formatTooltipDate(hoverInfo.previousPoint.actualDate)}
                    </div>
                    <div className='text-carbon text-sm font-medium'>
                      {tooltipFormatter(hoverInfo.previousPoint.rate)}
                    </div>
                  </div>

                  <div className='w-8 h-1 border-t-2 border-dashed border-[#949494]'></div>
                </div>
              )}
            </div>
          </div>
          {thresholdAlertConfig?.isAlertsSupported && (hoverInfo.currentPoint || hoverInfo.previousPoint) && (
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-50 border-t border-zinc-200 text-zinc-600 w-full'>
              <Icon IconComponent={BellRinging} size='sm' className='text-zinc-500' />
              <span className='text-xs font-medium text-coal-light'>
                Click to create alert at{' '}
                {tooltipFormatter(hoverInfo.currentPoint?.rate || hoverInfo.previousPoint?.rate || 0)}
              </span>
            </div>
          )}
        </TooltipInPortal>
      )}
    </div>
  )
}

export function ComparisonLineChart({
  data,
  isNoComparison,
  selectedTimeScale = 'daily',
  tooltipTitle = 'Identification Rate',
  yScaleType = 'percentage',
  isCurrency = false,
  variant = ChartVariant.DEFAULT,
  thresholdAlertConfig
}: {
  data: ComparisonLineChartDataType
  isNoComparison: boolean
  selectedTimeScale: SSOAnalyticsTimeScale
  tooltipTitle?: string
  yScaleType?: 'percentage' | 'linear'
  isCurrency?: boolean
  variant?: ChartVariant
  thresholdAlertConfig?: {
    isAlertsSupported: boolean
    onClickAlert: (rate: number) => void
  }
}) {
  return (
    <ParentSize>
      {({ width, height }) => (
        <LineChart
          width={width}
          height={height}
          data={data}
          isNoComparison={isNoComparison}
          yScaleType={yScaleType}
          selectedTimeScale={selectedTimeScale}
          tooltipTitle={tooltipTitle}
          isCurrency={isCurrency}
          variant={variant}
          thresholdAlertConfig={thresholdAlertConfig}
        />
      )}
    </ParentSize>
  )
}

export const LINE_CHART_EMPTY_DATA = {
  currentWeekData: [],
  previousWeekData: [],
  primaryDataset: [],
  secondaryDataset: [],
  isCurrentPrimary: false,
  currentDateRange: {
    from: new Date(),
    to: new Date()
  },
  previousDateRange: {
    from: new Date(),
    to: new Date()
  },
  totalConversionRatePercentage: 0,
  conversionRatePercentageChange: 0
}
